/**
 * deckProperties row validity guard.
 *
 * Two malformed row shapes have reached client stores in the past:
 *   - pre-Phase-5.3b `DODeckMeta` envelopes (`{ meta, updatedAtISO, … }` —
 *     no top-level `id`; cfa5c2da retired the wrapper code-only for the
 *     user side, and the outbound diff path ships storage rows unvalidated),
 *   - id-less `{ updatedAtISO }`-only minimal metas minted by the old
 *     `setDeckFromSync` for decks with no meta row yet.
 *
 * Either shape, once in the store, keys the map as
 * `deckProperties["undefined"]` (and pk `''` in the RN table router), hides
 * the deck from every deck list, and crashes consumers that read
 * `(p.deckPaths?.[0] ?? p.id)`.
 *
 * DELIBERATELY drop-only — no healing/unwrapping. A row that fails this
 * guard is discarded at the ingest and hydrate boundaries (with a warning),
 * which keeps the store crash-free without any timestamp or LWW side
 * effects. The ingest drop records no sync timestamp, so a future
 * server-side repair could still redeliver the row.
 */

import type { SyncDeckMeta } from './shared-types';

/** True when the row is a usable deckProperties entry: an object carrying a
 *  non-empty string `id`. Everything else (envelopes, id-less minimal metas,
 *  non-objects) must be dropped before it reaches the store. */
export function isValidDeckMetaRow(row: unknown): row is SyncDeckMeta {
  if (row === null || typeof row !== 'object' || Array.isArray(row)) return false;
  const id = (row as Record<string, unknown>).id;
  return typeof id === 'string' && id.length > 0;
}
